import React, { useEffect, useState, useMemo, useCallback, Suspense, lazy } from "react";
import { useNavigate } from "react-router-dom";
import * as Icon from "@phosphor-icons/react/dist/ssr";
import TopNavThree from "../../components/Header/TopNav/TopNavThree";
import MenuFurniture from "../../components/Header/Menu/MenuFurniture";
import MenuCategory from "../../components/Furniture/MenuCategory";
import SliderFurniture from "../../components/Slider/SliderFurniture";
import BannerTop from "../../components/Home3/BannerTop";
import Banner from "../../components/Furniture/Banner";
import FeatureProduct from "../../components/Furniture/FeatureProduct";
import FlashSale from "../../components/Furniture/FlashSale";
import TabFeatures from "../../components/Furniture/TabFeatures";
import Benefit from "../../components/Home1/Benefit";
import dataTestimonial from "../../data/Testimonial.json";
import VenteFlash from "../../components/Furniture/VenteFlash";
import BannerOffer from "../../components/Furniture/BannerOffer";
import BannerSpeacial from "../../components/Furniture/BannerSpeacial";
import { fetchProductTextWithCache } from "../../data/Productlist";
import Loading from "../../utils/Loading";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, EffectFade } from "swiper/modules";
import "swiper/css/bundle";
import "swiper/css/effect-fade";
import { useSliderImages } from "../../hooks/useSliderImages";
import { getSliderImageCacheBuster } from "../../utils/imageCacheBuster";

// Lazy load heavy components that are below the fold
const HomeProductGrid = lazy(() => import("../../components/Furniture/HomeProductGrid"));
const Testimonial = lazy(() => import("../../components/Underwear/Testimonial"));
const Instagram = lazy(() => import("../../components/Furniture/Instagram"));
const Footer = lazy(() => import("../../components/Footer/Footer"));

export default function HomeFurniture() {
  document.title = "Accueil | Kissaria"
  const [searchKeyword, setSearchKeyword] = useState("");
  const router = useNavigate();
  
  // Memoize search handler to prevent unnecessary re-renders
  const handleSearch = useCallback((value: string) => {
    if (value.trim()) {
      router(`/search-result?query=${value}`);
      setSearchKeyword("");
    }
  }, [router]);

  const [dataSourceProd, setDataSourceProd] = useState<any[]>([]);
  const [isLoadingText, setIsLoadingText] = useState(true);
  const { slidePaths, isLoading: isLoadingSlides } = useSliderImages();

  // Memoize filtered product data to prevent recalculation on every render
  const filteredProducts = useMemo(() => {
    return {
      pyjamas: dataSourceProd.filter((e) => (e?.category || "").toUpperCase() === "PYJAMAS- FEMME"),
      chales: dataSourceProd.filter((e) => (e?.category || "").toUpperCase() === "CHALES FOULARDS"),
      lingerie: dataSourceProd.filter((e) => (e?.category || "").toUpperCase() === "LINGERIE-FEMME"),
      accessBain: dataSourceProd.filter((e) => (e?.category || "").toUpperCase() === "ACCESS /BAIN"),
      bijoux: dataSourceProd.filter((e) => (e?.category || "").toUpperCase() === "BIJOUX"),
      accessoires: dataSourceProd.filter((e) => (e?.category || "").toUpperCase() === "ACCESSOIRES"),
      divers: dataSourceProd.filter((e) => {
        const category = (e?.category || "").toUpperCase();
        return category === "SERVIETTES" || category === "SOIN BEAUTE" || category === "CHAUSSETTES" || 
               category === "VAISSELLE" || category === "CHAUSSURES" || category === "VETEMENTS";
      })
    };
  }, [dataSourceProd]);

  const fetchData = useCallback(() => {
    fetchProductTextWithCache((products: any[]) => {
      setDataSourceProd(products);
      setIsLoadingText(false);
    });
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      <TopNavThree props="style-three bg-white" />
          
          {/* Mobile Search Section - moved above slider */}
          <div className="hidden max-sm:block bg-pink p-5 mb-4">
              <div className="form-search flex gap-3 items-center relative z-[1]">
                <input
                  type="text"
                  placeholder="Rechercher un article..."
                  className="border border-white text-white caption2 pr-10 p-3 placeholder-white outline-0 w-full bg-pink rounded-md"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch(searchKeyword);
                    }
                  }}
                />
                <span className="bg-orange border border-orange cursor-pointer p-3 px-7 flex justify-center items-center rounded-md">
                  <Icon.MagnifyingGlass
                    size={16}
                    className="text-white"
                    onClick={() => {
                      handleSearch(searchKeyword);
                    }}
                  />
                </span>
          </div>
        </div>
          
          <div id="header" className="relative max-sm:mb-5">
            <MenuFurniture props="bg-white" />
            <BannerTop
              props="bg-pink py-3 max-sm:hidden"
              textColor="text-white"
              bgLine="bg-white"
            />
            {/* Desktop/Tablet Slider */}
            <div className="max-sm:hidden">
              <SliderFurniture />
            </div>
            {/* Mobile Slider - same slides as desktop, only those that exist in /slider/ */}
            {!isLoadingSlides && slidePaths.length > 0 && (
              <div className="hidden max-sm:block p-0 m-0">
                <Swiper
                  navigation
                  grabCursor={true}
                  loop={slidePaths.length > 1}
                  autoplay={slidePaths.length > 1 ? { delay: 4000, disableOnInteraction: false } : false}
                  effect="fade"
                  fadeEffect={{ crossFade: true }}
                  speed={800}
                  modules={[Autoplay, Navigation, EffectFade]}
                  className="w-full h-[220px]"
                >
                  {slidePaths.map((path, i) => (
                    <SwiperSlide key={path}>
                      <img
                        src={getSliderImageCacheBuster(path)}
                        alt={`Slide ${i + 1}`}
                        className="w-full h-full object-fill"
                        loading={i === 0 ? "eager" : "lazy"}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}
          </div>
          <Benefit props="max-sm:hidden mt-4 md:mt-6 pt-4 pb-10 md:pt-6 md:pb-14" />
          <Banner />
          <VenteFlash />
          <BannerOffer />
          {/* Product section: show loading in section when user scrolls down while still loading */}
          <div className="w-[98%] m-auto my-6 min-h-[320px]">
            {isLoadingText && (
              <div className="flex flex-col items-center justify-center py-16 px-4 rounded-lg bg-gray-50 border border-gray-100">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mb-4" />
                <p className="text-gray-600 font-medium">Chargement des produits...</p>
                <p className="text-sm text-gray-400 mt-1">Veuillez patienter</p>
              </div>
            )}
            {!isLoadingText && dataSourceProd.length === 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-center text-amber-800">
                <p className="font-medium">Aucun produit chargé.</p>
                <p className="text-sm mt-1">Les produits viennent de <code className="bg-amber-100 px-1 rounded">/api/article.php</code>. Vérifiez l’onglet Réseau (Network) et que l’API renvoie du JSON avec <code className="bg-amber-100 px-1 rounded">{"{ \"products\": [...] }"}</code>.</p>
              </div>
            )}
            {!isLoadingText && dataSourceProd.length > 0 && (
              <Suspense fallback={
                <div className="flex flex-col items-center justify-center py-16 px-4 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mb-4" />
                  <p className="text-gray-600 font-medium">Chargement...</p>
                </div>
              }>
                <HomeProductGrid filteredProducts={filteredProducts} />
              </Suspense>
            )}
          </div>
          <Benefit props="hidden max-sm:block w-10/12 m-auto mt-4 pt-4 pb-8" />
          <BannerSpeacial />
          <Suspense fallback={<div className="min-h-[200px] flex items-center justify-center"><Loading /></div>}>
            <Testimonial data={dataTestimonial} limit={4} />
          </Suspense>
          <Suspense fallback={<div className="min-h-[200px] flex items-center justify-center"><Loading /></div>}>
            <Instagram />
          </Suspense>
          <Suspense fallback={<div className="min-h-[200px] flex items-center justify-center"><Loading /></div>}>
            <Footer />
          </Suspense>
    </>
  );
}
